import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';

import { PaymentModalPriceDetails } from '@/components/ClientTransactionDetails';
import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconDone from '@/icons/Done.svg';
import { useRequest } from '@/models/request';
import { useCancelTransaction, useTransaction, useValidateTransaction } from '@/models/transaction';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

export const PointOfSalesPaymentModal = ({
  transactionId,
  onClose,
}: {
  transactionId: string | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { request } = useRequestContext();

  const [manualMode, setManualMode] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [showReferenceError, setShowReferenceError] = useState(false);

  const { data: transaction } = useTransaction(transactionId, {
    enabled: !manualMode,
    refetchIntervalWhilePending: 5000,
  });

  const { mutateAsync: cancelTransaction, isPending: isCancelling } = useCancelTransaction();

  const {
    mutateAsync: validateTransaction,
    isPending: isValidating,
    isSuccess: isValidateSuccess,
  } = useValidateTransaction();

  const isSucceeded = transaction?.status === 'succeeded' || isValidateSuccess;

  /** This polling is used to update the article steps once the payment is validated, which in turn will unrender the modal */
  useRequest(request.id, {
    enabled: isSucceeded,
    refetchInterval: 5000,
  });

  const handleCancel = async () => {
    await cancelTransaction(transactionId!);
    onClose();
  };

  const handleManualValidate = async () => {
    if (paymentReference.length === 0) {
      setShowReferenceError(true);

      return;
    }

    await validateTransaction({
      transactionId: transactionId!,
      paymentReference: paymentReference || undefined,
    });
  };

  if (!transaction) {
    return null;
  }

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open && !isCancelling && !isValidating) {
          onClose();
        }
      }}
      title={t({
        id: 'request.pos-payment.title',
        message: 'POS Payment',
      })}
      dismissable={false}
    >
      <main>
        <Stack gap="16px">
          {isSucceeded ? (
            <Stack row flexWrap="nowrap" gap="1rem" alignItems="center">
              <IconDone style={{ fontSize: 42 }} className="color-primary-700" />
              <Paragraph>
                <Trans id="request.pos-payment.success">
                  The payment has been confirmed. You'll be redirected to the request in a few
                  seconds.
                </Trans>
              </Paragraph>
            </Stack>
          ) : (
            <Stack gap="12px">
              <Stack row flexWrap="nowrap" gap="1rem" alignItems="center">
                <Loader style={{ minHeight: 42, minWidth: 42 }} />
                <Paragraph>
                  <Trans id="request.pos-payment.polling">
                    A payment order has been sent to your store's cash register. Please process the
                    payment with the client to automatically validate this step.
                  </Trans>
                </Paragraph>
              </Stack>
              <PaymentModalPriceDetails
                prices={transaction.invoiceLines.map((invoiceLine) => invoiceLine.price)}
                showLabel
              />
              <Toggle
                alignment="left"
                isSelected={manualMode}
                onChange={setManualMode}
                label={
                  <Trans id="request.pos-payment.toggle-manual">
                    Enter payment information manually
                  </Trans>
                }
              />
              {manualMode && (
                <InputText
                  label={t({
                    id: 'request.pos-payment.reference-label',
                    message: 'Payment reference',
                  })}
                  value={paymentReference}
                  onChange={(value) => {
                    setPaymentReference(value);
                    setShowReferenceError(false);
                  }}
                  error={
                    showReferenceError && paymentReference.length === 0
                      ? t({
                          id: 'request.pos-payment.reference-error',
                          message: 'Payment reference is required',
                        })
                      : undefined
                  }
                  placeholder={t({
                    id: 'request.pos-payment.reference-placeholder',
                    message: 'i.e: OM260593',
                  })}
                />
              )}
            </Stack>
          )}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          {isSucceeded ? (
            <Button
              variant="secondary"
              onPress={() => {
                queryClient.invalidateQueries({ queryKey: ['requests'] });
                queryClient.invalidateQueries({ queryKey: ['client-payment-transactions'] });
                onClose();
              }}
            >
              <Trans id="request.pos-payment.close">Close</Trans>
            </Button>
          ) : manualMode ? (
            <>
              <Button
                variant="secondary"
                onPress={handleCancel}
                isLoading={isCancelling}
                disabled={isCancelling || isValidating}
              >
                <Trans id="request.pos-payment.cancel">Cancel payment</Trans>
              </Button>
              <Button
                variant="primary"
                onPress={handleManualValidate}
                isLoading={isValidating || isValidateSuccess}
                disabled={isCancelling || isValidating}
              >
                <Trans id="request.pos-payment.validate">Validate payment</Trans>
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onPress={handleCancel}
              isLoading={isCancelling}
              disabled={isCancelling}
            >
              <Trans id="request.pos-payment.cancel">Cancel payment</Trans>
            </Button>
          )}
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
